import type { Layout } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import {
  centroid,
  distancePointSegment,
  minAreaOBB,
  pointInRing,
  ringBounds,
} from '../../../geo/polygon';
import { landmarkObstacleRings } from '../../fabric/landmarks/landmarkObstacles';
import { marketStallFronts } from '../../fabric/landmarks/marketStalls';
import { placeQuayGoods, QUAY_GOODS } from '../../streetscape/quayGoods';
import { alongside, circle, pitch, SEATS, type Seat } from './seats';
import { passable, roomAt, WALK_NET, type Ground } from './walkNet';
import { lastageYardGround } from './workGround';

/**
 * Where the crowd goes. Each kind is a reason to be out of doors that the city of 1538 gave its
 * people: a seller's pitch on the market squares, a place at the fish benches, the open square to
 * talk on, work on the quays and at the goods stacked there, the Lastage's yard, the churches and
 * chapels, the ground before a religious house or a church where the religious stand (`cloister`),
 * a street corner, and the gates for those with business outside the walls. What was sold where on the squares is not reconstructed: the pitches stand
 * in rows because a market does, not after a source.
 */
export const PLACE_KINDS = [
  'market',
  'quay',
  'church',
  'corner',
  'gate',
  'stall',
  'square',
  'yard',
  'load',
  'cloister',
] as const;
export type PlaceKind = (typeof PLACE_KINDS)[number];

/** The religious houses' people, by what they are drawn as (docs/CITIZENS.md). */
export const ORDERS = ['sister', 'friar', 'beguine'] as const;
export type Order = (typeof ORDERS)[number];

export const PLACES = {
  /** One place every so many metres of quay, and of street. */
  quayEveryM: 45,
  cornerEveryM: 40,
  /** How near a point of the net must lie to a church's walls or a gate. */
  churchReachM: 40,
  gateReachM: 45,
  /** A door's own street point: within this far, the nearest it reaches over open ground. */
  doorReachM: 16,
  doorStepM: 1,
  /** A pitch needs this much room round its point, for the seller and those who buy. */
  pitchRoomM: 1.8,
  /** A seller at a bench stands this far out from its edge. */
  benchOffM: 0.45,
  /** How far a point of the net may lie from a place at a bench, at work or at a stack. */
  benchReachM: 9,
  workReachM: 9,
  loadReachM: 7,
  /** A religious house's own place is the point before its door, or one this near it. */
  cloisterReachM: 12,
  /** No two seats of the city are nearer each other than a body is wide. */
  seatsApartM: 0.6,
  /** The beguines live behind the house doors nearest their chapel: this many, this near. */
  beguineDoors: 12,
  beguineReachM: 80,
} as const;

/** The net while it is planned: its points, x, y and free half-width each, and its joins. */
export interface Draft {
  readonly nodes: readonly number[];
  readonly edges: readonly number[];
}

export function nodeAt(draft: Draft, i: number): Vec2 {
  return [draft.nodes[i * 3]!, draft.nodes[i * 3 + 1]!];
}

/** The points of the net within `reach` of a place, nearest first. */
export function nodesNear(draft: Draft, p: Vec2, reach: number): number[] {
  const found: [number, number][] = [];
  for (let i = 0; i < draft.nodes.length / 3; i++) {
    const gap = Math.hypot(draft.nodes[i * 3]! - p[0], draft.nodes[i * 3 + 1]! - p[1]);
    if (gap <= reach) found.push([gap, i]);
  }
  return found.sort((a, b) => a[0] - b[0] || a[1] - b[1]).map(([, i]) => i);
}

function ringDistance(p: Vec2, ring: Ring): number {
  if (pointInRing(p, ring)) return 0;
  return Math.min(
    ...ring.map((a, i) => distancePointSegment(p, { a, b: ring[(i + 1) % ring.length]! }))
  );
}

/* Points every `spacing` metres along a line, from half a spacing in. */
function every(line: readonly Vec2[], spacing: number): Vec2[] {
  const points: Vec2[] = [];
  let due = spacing / 2;
  line.forEach((a, i) => {
    const b = line[i + 1];
    if (!b) return;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    while (due <= length) {
      points.push([a[0] + ((b[0] - a[0]) * due) / length, a[1] + ((b[1] - a[1]) * due) / length]);
      due += spacing;
    }
    due -= length;
  });
  return points;
}

/** The door of a religious house: where its people step out, and the point of the net before it. */
export interface CloisterGate {
  readonly position: Vec2;
  readonly outward: Vec2;
  readonly node: number;
  readonly order: Order;
}

/* The houses of men, by their names in the layout; every other religious house is of women. */
const BROTHERS = /broeders|kartuizer|regulier/;

/* The Clarissen lived enclosed (src/world/life/README.md): their house has no door on the street,
   and none of them walks the city. */
const ENCLOSED = /clarissen/;

function nearestOn(ring: Ring, p: Vec2): Vec2 {
  let best: Vec2 = ring[0]!,
    gap = Infinity;
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
    const t = Math.max(
      0,
      Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy || 1))
    );
    const q: Vec2 = [a[0] + dx * t, a[1] + dy * t];
    const d = Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (d < gap) [gap, best] = [d, q];
  });
  return best;
}

/**
 * A door for each religious house the net comes to: the point of its walls nearest a point of the
 * net that a body can reach from there. The layout draws no gate for these houses, so where each
 * stood is not claimed; a ruined house, an enclosed one and one the net does not reach have none.
 */
export function cloisterGates(layout: Layout, draft: Draft, ground: Ground): CloisterGate[] {
  const gates: CloisterGate[] = [];
  for (const house of layout.landmarks) {
    const convent = house.kind === 'monastery' || /broeders/.test(house.id);
    if (!convent || house.params?.['ruin'] === true || ENCLOSED.test(house.id)) continue;
    const walls = landmarkObstacleRings(house);
    const rings = walls.length > 0 ? walls : [house.footprint];
    const middle = centroid(house.footprint);
    const radius = Math.max(
      ...rings.flat().map((p) => Math.hypot(p[0] - middle[0], p[1] - middle[1]))
    );
    let found: Omit<CloisterGate, 'order'> | undefined,
      gap: number = PLACES.doorReachM;
    for (const node of nodesNear(draft, middle, radius + PLACES.doorReachM)) {
      const p = nodeAt(draft, node);
      for (const ring of rings) {
        const position = nearestOn(ring, p);
        const d = Math.hypot(p[0] - position[0], p[1] - position[1]);
        if (d >= gap || d < PLACES.doorStepM) continue;
        const outward: Vec2 = [(p[0] - position[0]) / d, (p[1] - position[1]) / d];
        const step: Vec2 = [
          position[0] + outward[0] * PLACES.doorStepM,
          position[1] + outward[1] * PLACES.doorStepM,
        ];
        if (!ground.open(step) || !passable(step, p, ground.open)) continue;
        [gap, found] = [d, { position, outward, node }];
      }
    }
    if (found) gates.push({ ...found, order: BROTHERS.test(house.id) ? 'friar' : 'sister' });
  }
  return gates;
}

export interface PlannedSeat extends Seat {
  /** Which of the planned places it is a seat of. */
  readonly place: number;
}

export interface PlannedPlaces {
  readonly places: readonly (readonly [PlaceKind, number])[];
  readonly seats: readonly PlannedSeat[];
}

/* The golden angle: neighbouring circles are turned unlike each other. */
const TURN = 2.399963;

/** The places of the city on the net, and where people stand at each. */
export function planPlaces(
  layout: Layout,
  draft: Draft,
  ground: Ground,
  gates: readonly CloisterGate[],
  isWater: (p: Vec2) => boolean
): PlannedPlaces {
  const places: [PlaceKind, number][] = [];
  const seats: PlannedSeat[] = [];
  const placeAt = new Map<number, number>();
  let groups = 0;
  const add = (kind: PlaceKind, node: number | undefined): number => {
    if (node === undefined || placeAt.has(node)) return -1;
    placeAt.set(node, places.length);
    places.push([kind, node]);
    return places.length - 1;
  };
  const stands = (place: number, seat: Seat): boolean =>
    ground.open(seat.at) &&
    passable(nodeAt(draft, places[place]![1]), seat.at, ground.open) &&
    // Two things worked at may stand side by side: no seat is laid in another's.
    seats.every(
      (other) =>
        Math.hypot(other.at[0] - seat.at[0], other.at[1] - seat.at[1]) >= PLACES.seatsApartM
    );
  /* Seats laid out together keep their companies. One that cannot be stood on or walked to from
     the place's point is left out; with `whole`, so are the others laid out with it. */
  const seat = (place: number, laid: readonly Seat[], whole = false): void => {
    if (place < 0) return;
    const good = laid.filter((one) => stands(place, one));
    if (whole && good.length < laid.length) return;
    for (const one of good) seats.push({ ...one, place, group: groups + one.group });
    groups += Math.max(-1, ...laid.map((one) => one.group)) + 1;
  };
  /* The place of a kind nearest a point that a body can reach the point from: one already
     there, or a new one. */
  const near = (kind: PlaceKind, p: Vec2, reach: number): number => {
    for (const node of nodesNear(draft, p, reach)) {
      if (!passable(nodeAt(draft, node), p, ground.open)) continue;
      const known = placeAt.get(node);
      if (known === undefined) return add(kind, node);
      if (places[known]![0] === kind) return known;
    }
    return -1;
  };
  const ringed = (kind: PlaceKind, node: number | undefined): void => {
    const place = add(kind, node);
    if (place < 0) return;
    const p = nodeAt(draft, node!);
    seat(place, circle(p, roomAt(p, ground), node! * TURN));
  };

  // The fish benches: a seller with its back to each side of each place, its buyers before it.
  for (const stalls of layout.landmarks.filter((l) => l.kind === 'marketStalls'))
    for (const front of marketStallFronts(stalls, () => undefined)) {
      const off = PLACES.benchOffM + SEATS.pitch.back;
      const laid = pitch(
        [front.at[0] + front.out[0] * off, front.at[1] + front.out[1] * off],
        Math.atan2(front.out[1], front.out[0])
      );
      seat(near('stall', laid[1]!.at, PLACES.benchReachM), laid, true);
    }
  // The squares: rows of pitches, each row facing the next across a lane, and between the rows
  // room to stand and talk.
  for (const square of layout.squares) {
    const { minX, minY } = ringBounds(square.polygon);
    for (let i = 0; i < draft.nodes.length / 3; i++) {
      const p = nodeAt(draft, i);
      if (!pointInRing(p, square.polygon)) continue;
      const [across, up] = [(p[0] - minX) / WALK_NET.squareM, (p[1] - minY) / WALK_NET.squareM];
      const [col, row] = [Math.round(across), Math.round(up)];
      // Only the points the square itself is covered with stand in rows.
      if (Math.abs(across - col) > 0.01 || Math.abs(up - row) > 0.01) continue;
      if (row % 2 === 0 && roomAt(p, ground) >= PLACES.pitchRoomM) {
        const laid = pitch(p, row % 4 === 0 ? Math.PI / 2 : -Math.PI / 2);
        if (laid.every((one) => ground.open(one.at) && passable(p, one.at, ground.open)))
          seat(add('market', i), laid, true);
      } else if (row % 2 === 1 && col % 2 === 1) ringed('square', i);
    }
  }
  for (const gate of gates) {
    const p = nodeAt(draft, gate.node);
    ringed(
      'cloister',
      nodesNear(draft, p, PLACES.cloisterReachM).find(
        (node) => !placeAt.has(node) && passable(p, nodeAt(draft, node), ground.open)
      )
    );
  }
  for (const church of layout.landmarks.filter((l) => l.kind === 'church' || l.kind === 'chapel')) {
    const near = nodesNear(draft, centroid(church.footprint), PLACES.churchReachM + 40).filter(
      (i) => ringDistance(nodeAt(draft, i), church.footprint) <= PLACES.churchReachM
    );
    const [door, beside] = near
      .sort(
        (a, b) =>
          ringDistance(nodeAt(draft, a), church.footprint) -
          ringDistance(nodeAt(draft, b), church.footprint)
      )
      .filter((node) => !placeAt.has(node));
    add('church', door);
    // Before a church, as before their own door, the religious stand a while.
    ringed('cloister', beside);
  }
  for (const gate of layout.fortifications.gates)
    add('gate', nodesNear(draft, gate.pos, PLACES.gateReachM)[0]);
  // The yard's hulls and timber, and the goods on the quays: hands at their sides.
  for (const work of lastageYardGround().works)
    for (const hand of alongside(work.box, work.most))
      seat(near('yard', hand.at, PLACES.workReachM), [hand]);
  for (const goods of placeQuayGoods(QUAY_GOODS, layout.streets, isWater))
    for (const hand of alongside(minAreaOBB(goods.footprint), 1))
      seat(near('load', hand.at, PLACES.loadReachM), [hand]);
  for (const street of layout.streets) {
    const quay = street.kind === 'quay';
    if (!quay && street.kind !== 'street' && street.kind !== 'dike') continue;
    for (const p of every(street.centerline, quay ? PLACES.quayEveryM : PLACES.cornerEveryM))
      ringed(quay ? 'quay' : 'corner', nodesNear(draft, p, 4)[0]);
  }
  return { places, seats };
}
